import type { ComputedRef } from 'vue'
import type { RouteLocationAsRelative } from 'vue-router'

import { ROUTE_COMMUNITY } from '~/contexts/community/router/routes'
import { ROUTE_DM } from '~/contexts/dm/router/routes'
import { ROUTE_HOME } from '~/contexts/home/router/routes'
import { ROUTE_NETWORK } from '~/contexts/network/router/routes'
import { ROUTE_RITUAL } from '~/contexts/ritual/router/routes'

import { router } from '~/router/router'

import { useContentContextManager, useContextManager } from '~/features-shared/config-manager/composables/context-manager'
import type { TContentContext } from '~/features-shared/config-manager/interfaces/contexts'
import { EContentContext, EContext } from '~/features-shared/config-manager/interfaces/contexts'
import type { IContentModel } from '~/features-shared/content/interfaces/content-model'
import type { MediaDerivedType } from '~/features-shared/content-derived-type/interfaces/derived-types'
import type { Acl } from '~/features-shared/libs/interfaces/authorization'

import { useTranslations } from '~/features-generic/translations/composables/translations'

import type { NlsContentTypes } from '~/typings/nls/content-types'

export interface _ContentType {
  text: string
  model: TContentContext
  derivedTypeId?: string
  singular: string
  fonticon: string
  color: string
  bgcolor: string
  bordercolor: string
  order: number
  canDraft?: boolean
  autosave?: {
    creation?: boolean
    edition?: boolean
  }
  canExportAsPdf?: boolean
  routes: Partial<Record<EContext, RouteLocationAsRelative>>
  backend?: {
    is_enabled_token?: string
    min_acl_token?: string
    default_type_for_creation?: string
  }
  acl?: Acl
  media_type?: MediaDerivedType
  // parent type of derived content
  parent?: IContentModel
}
export interface ContentType extends _ContentType {
  create: string
  selected: boolean
  to?: RouteLocationAsRelative
  from?: RouteLocationAsRelative
}

const _MESSAGE: _ContentType = {
  text: 'Messages',
  singular: 'Message',
  model: EContentContext.Message,
  fonticon: 'chat',
  color: 'text-color-message',
  bgcolor: 'bg-color-message',
  bordercolor: 'border-color-message',
  order: 0,
  canDraft: false,
  autosave: {},
  routes: {},
}

const _COMMENT: _ContentType = {
  text: 'Comments',
  singular: 'Comment',
  model: EContentContext.Comment,
  fonticon: 'chat',
  color: 'text-color-message',
  bgcolor: 'bg-color-message',
  bordercolor: 'border-color-message',
  order: 0,
  canDraft: false,
  autosave: {},
  routes: {},
}
const _ANSWER: _ContentType = {
  text: 'Answers',
  singular: 'Answer',
  model: EContentContext.Answer,
  fonticon: 'chat',
  color: 'text-color-message',
  bgcolor: 'bg-color-message',
  bordercolor: 'border-color-message',
  order: 0,
  canDraft: false,
  autosave: {},
  routes: {},
}
const _POST: _ContentType = {
  text: 'Posts',
  singular: 'Post',
  model: EContentContext.Post,
  fonticon: 'newspaper',
  color: 'text-color-post',
  bgcolor: 'bg-color-post',
  bordercolor: 'border-color-post',
  canDraft: true,
  canExportAsPdf: true,
  order: 1,
  autosave: {
    creation: true,
    edition: true,
  },
  routes: {
    // @ts-expect-error 'thread_id' is missing but this is expected in this case
    [EContext.COMMUNITY]: { name: ROUTE_COMMUNITY.URI_FACETS_CONTENT, params: { content_type: EContentContext.Post } },
    // @ts-expect-error 'thread_id' is missing but this is expected in this case
    [EContext.DM]: { name: ROUTE_DM.URI_FACETS_CONTENT, params: { content_type: EContentContext.Post } },
  },
  backend: {
    is_enabled_token: 'post_is_enabled',
    min_acl_token: 'post_min_acl',
    default_type_for_creation: 'Post',
  },
}

const _MEDIA: _ContentType = {
  text: 'Media',
  singular: 'Media',
  model: EContentContext.Media,
  fonticon: 'picture',
  color: 'text-color-media',
  bgcolor: 'bg-color-media',
  bordercolor: 'border-color-media',
  canDraft: true,
  order: 2,
  autosave: {},
  routes: {
    [EContext.COMMUNITY]: { name: ROUTE_COMMUNITY.URI_FACETS_MEDIA },
    [EContext.DM]: { name: ROUTE_DM.URI_FACETS_MEDIA },
  },
  backend: {
    is_enabled_token: 'media_is_enabled',
    min_acl_token: 'media_min_acl',
    default_type_for_creation: 'Media',
  },
}

const _WEDO: _ContentType = {
  text: 'Wedos',
  singular: 'Wedo',
  model: EContentContext.Wedo,
  fonticon: 'task',
  color: 'text-color-wedo',
  bgcolor: 'bg-color-wedo',
  bordercolor: 'border-color-wedo',
  canDraft: false,
  order: 6,
  autosave: {},
  routes: {
    [EContext.COMMUNITY]: { name: ROUTE_COMMUNITY.URI_FACETS_WEDO },
    [EContext.DM]: { name: ROUTE_DM.URI_FACETS_WEDO },
  },
  backend: {
    is_enabled_token: 'wedo_is_enabled',
    min_acl_token: 'wedo_min_acl',
    default_type_for_creation: 'Wedo',
  },
}

const _CONFERENCE: _ContentType = {
  text: 'Conferences',
  singular: 'Conference',
  model: EContentContext.Conference,
  fonticon: 'calendar',
  color: 'text-color-conference',
  bgcolor: 'bg-color-conference',
  bordercolor: 'border-color-conference',
  canDraft: false,
  order: 8,
  autosave: {},
  routes: {
    // @ts-expect-error 'thread_id' is missing but this is expected in this case
    [EContext.COMMUNITY]: { name: ROUTE_COMMUNITY.URI_FACETS_CONTENT, params: { content_type: EContentContext.Conference } },
    // @ts-expect-error 'thread_id' is missing but this is expected in this case
    [EContext.DM]: { name: ROUTE_DM.URI_FACETS_CONTENT, params: { content_type: EContentContext.Conference } },
  },
  backend: {
    is_enabled_token: 'conference_is_enabled',
    min_acl_token: 'conference_min_acl',
    default_type_for_creation: 'Conference',
  },
}

const _IDEA: _ContentType = {
  text: 'Ideas',
  singular: 'Idea',
  model: EContentContext.Idea,
  fonticon: 'lamp',
  color: 'text-color-idea',
  bgcolor: 'bg-color-idea',
  bordercolor: 'border-color-idea',
  canDraft: true,
  canExportAsPdf: true,
  order: 3,
  autosave: {
    creation: true,
    edition: true,
  },
  routes: {
    [EContext.COMMUNITY]: { name: ROUTE_COMMUNITY.URI_FACETS_IDEA },
  },
  backend: {
    is_enabled_token: 'ideation_is_enabled',
    min_acl_token: 'ideation_min_acl',
    default_type_for_creation: 'Idea',
  },
}

const _QUESTION: _ContentType = {
  text: 'Questions',
  singular: 'Question',
  model: EContentContext.Question,
  fonticon: 'comment-question',
  color: 'text-color-question',
  bgcolor: 'bg-color-question',
  bordercolor: 'border-color-question',
  canDraft: true,
  canExportAsPdf: true,
  order: 4,
  autosave: {
    creation: true,
    edition: true,
  },
  routes: {
    [EContext.COMMUNITY]: { name: ROUTE_COMMUNITY.URI_FACETS_QUESTION },
  },
  backend: {
    is_enabled_token: 'qna_is_enabled',
    min_acl_token: 'qna_min_acl',
    default_type_for_creation: 'Question',
  },
}

const _WIKI: _ContentType = {
  text: 'Wikis',
  singular: 'Wiki',
  model: EContentContext.Wiki,
  fonticon: 'book-open',
  color: 'text-color-wiki',
  bgcolor: 'bg-color-wiki',
  bordercolor: 'border-color-wiki',
  canDraft: false,
  canExportAsPdf: true,
  order: 5,
  autosave: {
    creation: false,
    edition: true,
  },
  routes: {
    [EContext.COMMUNITY]: { name: ROUTE_COMMUNITY.URI_FACETS_WIKI },
  },
  backend: {
    is_enabled_token: 'wiki_is_enabled',
    min_acl_token: 'wiki_min_acl',
    default_type_for_creation: 'Wiki',
  },
}

const _SURVEY: _ContentType = {
  text: 'Surveys',
  singular: 'Survey',
  model: EContentContext.Survey,
  fonticon: 'clipboard',
  color: 'text-color-survey',
  bgcolor: 'bg-color-survey',
  bordercolor: 'border-color-survey',
  canDraft: true,
  order: 7,
  autosave: {},
  routes: {
    // @ts-expect-error 'thread_id' is missing but this is expected in this case
    [EContext.COMMUNITY]: { name: ROUTE_COMMUNITY.URI_FACETS_CONTENT, params: { content_type: EContentContext.Survey } },
  },
  backend: {
    is_enabled_token: 'survey_is_enabled',
    min_acl_token: 'survey_min_acl',
    default_type_for_creation: 'Survey',
  },
}

const _RITUAL: _ContentType = {
  text: 'Rituals',
  singular: 'Ritual',
  model: EContentContext.Rituals, // TODO we may need to change this to EContentContext.Rituals but for now it's good as a ritual is a special conference & it's contetnType is 'conference' in the backend
  fonticon: 'calendar-clock',
  color: 'text-color-conference',
  bgcolor: 'bg-color-conference',
  bordercolor: 'border-color-conference',
  canDraft: false,
  order: 0,
  autosave: {},
  routes: {
    [EContext.COMMUNITY]: { name: ROUTE_RITUAL.URI_HOME },
  },
  canExportAsPdf: false,
}

// Streaming pseudo-type : this type will only be used to create VBrick Meeting Streaming, it will look like a Classic meeting but with extra step
// it will only be visible to the people in the user group, this only be manageable via the community setting (not sure yet 8/1/24)
const _STREAMING: _ContentType = {
  text: 'Streamings',
  singular: 'Streaming',
  model: EContentContext.Streaming,
  fonticon: 'calendar',
  color: 'text-color-conference',
  bgcolor: 'bg-color-conference',
  bordercolor: 'border-color-conference',
  canDraft: false,
  order: 9,
  autosave: {},
  routes: {
    // @ts-expect-error 'thread_id' is missing but this is expected in this case
    [EContext.COMMUNITY]: { name: ROUTE_COMMUNITY.URI_FACETS_CONTENT, params: { content_type: EContentContext.Conference } },
  },
  backend: {
    is_enabled_token: 'conference_is_enabled',
    min_acl_token: 'conference_min_acl',
    default_type_for_creation: 'Conference',
  },
}

export function getPathFromName(_context: EContext): RouteLocationAsRelative {
  const { context } = useContentContextManager()
  if (_context === EContext.COMMUNITY)
    return { name: ROUTE_COMMUNITY.URI_HOME }
  if (_context === EContext.DM)
    return { name: ROUTE_DM.URI_HOME }
  if (_context === EContext.ME)
    return { name: `me-${context.value}-index` } as RouteLocationAsRelative
  if (_context === EContext.NETWORK)
    return { name: ROUTE_NETWORK.USER_HOME }
  return { name: ROUTE_HOME.LANDING }
}
export function getRouteParams(context: EContext, content: _ContentType): RouteLocationAsRelative {
  return content.routes[context] || { name: ROUTE_HOME.LANDING }
}

export function getPathFromRoute(route?: RouteLocationAsRelative): string {
  if (!route)
    return router.resolve({ name: ROUTE_HOME.LANDING }).path

  try {
    return router.resolve(route).path
  }
  catch {
    return router.resolve({ name: ROUTE_HOME.LANDING }).path
  }
}

/**
 * Enumerate the different types of content
 */
export enum ContentTypeName {
  ANSWER = 'ANSWER',
  COMMENT = 'COMMENT',
  MESSAGE = 'MESSAGE',
  POST = 'POST',
  MEDIA = 'MEDIA',
  IDEA = 'IDEA',
  QUESTION = 'QUESTION',
  WIKI = 'WIKI',
  WEDO = 'WEDO',
  SURVEY = 'SURVEY',
  CONFERENCE = 'CONFERENCE',
  RITUAL = 'RITUAL',
  STREAMING = 'STREAMING',
}

const _TYPES: Record<ContentTypeName, _ContentType> = {
  [ContentTypeName.ANSWER]: _ANSWER,
  [ContentTypeName.COMMENT]: _COMMENT,
  [ContentTypeName.MESSAGE]: _MESSAGE,
  [ContentTypeName.POST]: _POST,
  [ContentTypeName.MEDIA]: _MEDIA,
  [ContentTypeName.IDEA]: _IDEA,
  [ContentTypeName.QUESTION]: _QUESTION,
  [ContentTypeName.WIKI]: _WIKI,
  [ContentTypeName.WEDO]: _WEDO,
  [ContentTypeName.SURVEY]: _SURVEY,
  [ContentTypeName.CONFERENCE]: _CONFERENCE,
  [ContentTypeName.RITUAL]: _RITUAL,
  [ContentTypeName.STREAMING]: _STREAMING,
}

export function getContentTypeNotProcessedFromModel(model: TContentContext) {
  return Object.values(_TYPES)
    .find(el => el.model === model)
}

export function getContentTypeNotProcessed(contentTypeName: ContentTypeName) {
  return _TYPES[contentTypeName]
}

export function useContentTypes() {
  const { $i18n } = useTranslations('content-types')
  const { context } = useContextManager()
  const route = useRoute()

  const TYPES: Record<string, ComputedRef<ContentType>> = Object.fromEntries(Object.values(_TYPES)
    .map((el) => {
      return [el.singular.toUpperCase(), computed(() => {
        const contentRoute = getRouteParams(context.value, el)
        const path = getPathFromRoute(contentRoute)
        const _from = getPathFromName(context.value)
        return {
          ...el,
          to: contentRoute,
          from: _from,
          selected: route?.path === path,
          text: $i18n(el.text as keyof NlsContentTypes),
          singular: $i18n(el.singular as keyof NlsContentTypes),
          create: $i18n(`create${el.singular}` as keyof NlsContentTypes),
        }
      })]
    }))
  const getContentType = (model: TContentContext) => Object.values(TYPES)
    .find(el => el.value.model === model)
  return {
    [ContentTypeName.ANSWER]: TYPES.ANSWER,
    [ContentTypeName.COMMENT]: TYPES.COMMENT,
    [ContentTypeName.MESSAGE]: TYPES.MESSAGE,
    [ContentTypeName.POST]: TYPES.POST,
    [ContentTypeName.MEDIA]: TYPES.MEDIA,
    [ContentTypeName.IDEA]: TYPES.IDEA,
    [ContentTypeName.QUESTION]: TYPES.QUESTION,
    [ContentTypeName.WIKI]: TYPES.WIKI,
    [ContentTypeName.WEDO]: TYPES.WEDO,
    [ContentTypeName.SURVEY]: TYPES.SURVEY,
    [ContentTypeName.CONFERENCE]: TYPES.CONFERENCE,
    [ContentTypeName.RITUAL]: TYPES.RITUAL,
    [ContentTypeName.STREAMING]: TYPES.STREAMING,
    getContentType,
  }
}
